import { apiRequest } from '../../../shared/services/apiClient';
import { FALLBACK_CURRENCY } from '../invoiceForm';
import {
  CreateInvoicePaymentRequest,
  CurrencyOption,
  CreateInvoiceRequest,
  Invoice,
  InvoiceDocumentType,
  InvoiceLine,
  InvoiceLineType,
  InvoiceListItem,
  InvoiceListQuery,
  InvoicePagedResult,
  InvoicePayment,
  InvoicePaymentMethod,
  InvoicePaymentTotals,
  InvoiceDaySummary,
  InvoiceSettings,
  InvoiceStatus,
  SendInvoiceEmailRequest,
  UpdateInvoiceRequest,
  UpdateInvoiceSettingsRequest,
  CashDay,
  CashDayAdjustment,
  CashAdjustmentType,
  CashDayStatus,
  UpsertCashDayRequest,
  CreateCashDayAdjustmentRequest,
} from '../types/Invoice';

interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  hasNext: boolean;
}

const documentTypes: InvoiceDocumentType[] = ['invoice', 'creditNote'];
const statuses: InvoiceStatus[] = ['draft', 'issued', 'partiallyPaid', 'paid', 'void'];
const lineTypes: InvoiceLineType[] = ['product', 'service', 'custom'];
const paymentMethods: InvoicePaymentMethod[] = ['cash', 'card', 'bank', 'other', 'creditNote', 'online'];
const cashDayStatuses: CashDayStatus[] = ['draft', 'finalized'];
const cashAdjustmentTypes: CashAdjustmentType[] = ['pettyCash', 'cashTopUp', 'bankedCash', 'variance'];

const optionalText = (value: unknown): string | undefined => {
  const text = String(value ?? '').trim();
  return text || undefined;
};

const optionalNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const asNumber = (value: unknown, fallback = 0): number => optionalNumber(value) ?? fallback;

const normalizeEnum = <T extends string>(value: unknown, allowed: T[], fallback: T): T => {
  const normalized = String(value ?? '');
  const camel = normalized.charAt(0).toLowerCase() + normalized.slice(1);
  return allowed.includes(camel as T) ? camel as T : fallback;
};

const normalizeLine = (value: any): InvoiceLine => ({
  id: String(value?.id ?? ''),
  lineType: normalizeEnum(value?.lineType, lineTypes, 'custom'),
  productId: optionalText(value?.productId),
  serviceOfferingId: optionalText(value?.serviceOfferingId),
  name: String(value?.name ?? ''),
  description: optionalText(value?.description),
  code: optionalText(value?.code),
  unit: optionalText(value?.unit),
  quantity: asNumber(value?.quantity, 1),
  unitPrice: asNumber(value?.unitPrice),
  discountPercent: asNumber(value?.discountPercent),
  taxPercent: asNumber(value?.taxPercent),
  lineSubtotal: asNumber(value?.lineSubtotal),
  lineTax: asNumber(value?.lineTax),
  lineTotal: asNumber(value?.lineTotal),
  sequence: asNumber(value?.sequence),
});

const normalizePayment = (value: any): InvoicePayment => ({
  id: String(value?.id ?? ''),
  method: normalizeEnum(value?.method, paymentMethods, 'other'),
  amount: asNumber(value?.amount),
  paidAt: String(value?.paidAt ?? ''),
  note: optionalText(value?.note),
  creditNoteId: optionalText(value?.creditNoteId),
  createdAt: optionalText(value?.createdAt),
  updatedAt: optionalText(value?.updatedAt),
});

const paymentTotals = (value: any): InvoicePaymentTotals => ({
  total: asNumber(value?.total),
  cash: asNumber(value?.cash),
  card: asNumber(value?.card),
  bank: asNumber(value?.bank),
  other: asNumber(value?.other),
});

const normalizeCurrencyCode = (value: unknown): string =>
  String(value ?? '').trim().toUpperCase() || FALLBACK_CURRENCY;

const normalizeCurrencyOption = (value: any): CurrencyOption => ({
  code: normalizeCurrencyCode(value?.code),
  name: String(value?.name ?? value?.code ?? ''),
  symbol: String(value?.symbol ?? ''),
  minorUnit: optionalText(value?.minorUnit),
});

const normalizeCashDayAdjustment = (value: any): CashDayAdjustment => ({
  id: String(value?.id ?? ''),
  type: normalizeEnum(value?.type, cashAdjustmentTypes, 'variance'),
  amount: asNumber(value?.amount),
  note: optionalText(value?.note),
  createdAt: optionalText(value?.createdAt),
  updatedAt: optionalText(value?.updatedAt),
});

const normalizeCashDay = (value: any): CashDay => ({
  id: optionalText(value?.id),
  date: String(value?.date ?? ''),
  locationId: optionalText(value?.locationId),
  currency: normalizeCurrencyCode(value?.currency),
  status: normalizeEnum(value?.status, cashDayStatuses, 'draft'),
  openingCash: asNumber(value?.openingCash),
  closingCash: optionalNumber(value?.closingCash),
  terminalCardPayments: asNumber(value?.terminalCardPayments),
  openingNote: optionalText(value?.openingNote),
  closingNote: optionalText(value?.closingNote),
  notes: optionalText(value?.notes),
  previousClosingCash: optionalNumber(value?.previousClosingCash),
  adjustments: Array.isArray(value?.adjustments) ? value.adjustments.map(normalizeCashDayAdjustment) : [],
  payments: paymentTotals(value?.payments),
  adjustmentTotal: asNumber(value?.adjustmentTotal),
  expectedCash: asNumber(value?.expectedCash),
  cashDifference: optionalNumber(value?.cashDifference),
  cardDifference: asNumber(value?.cardDifference),
  updatedAt: optionalText(value?.updatedAt),
});

const normalizeInvoice = (value: any): Invoice => ({
  id: String(value?.id ?? ''),
  documentType: normalizeEnum(value?.documentType, documentTypes, 'invoice'),
  status: normalizeEnum(value?.status, statuses, 'draft'),
  number: optionalText(value?.number),
  sequentialNumber: optionalNumber(value?.sequentialNumber),
  issueDate: optionalText(value?.issueDate),
  dueDate: optionalText(value?.dueDate),
  currency: normalizeCurrencyCode(value?.currency),
  pricesIncludeTax: Boolean(value?.pricesIncludeTax),
  contactId: String(value?.contactId ?? ''),
  contactName: String(value?.contactName ?? value?.billToName ?? ''),
  assetId: optionalText(value?.assetId),
  assetName: optionalText(value?.assetName),
  locationId: optionalText(value?.locationId),
  locationName: optionalText(value?.locationName),
  sourceType: optionalText(value?.sourceType),
  sourceId: optionalText(value?.sourceId),
  relatedInvoiceId: optionalText(value?.relatedInvoiceId),
  relatedInvoiceNumber: optionalText(value?.relatedInvoiceNumber),
  billToName: String(value?.billToName ?? ''),
  billToEmail: optionalText(value?.billToEmail),
  billToPhone: optionalText(value?.billToPhone),
  billToAddressLine1: optionalText(value?.billToAddressLine1),
  billToAddressLine2: optionalText(value?.billToAddressLine2),
  billToCity: optionalText(value?.billToCity),
  billToStateOrProvince: optionalText(value?.billToStateOrProvince),
  billToPostalCode: optionalText(value?.billToPostalCode),
  billToCountry: optionalText(value?.billToCountry),
  sellerName: optionalText(value?.sellerName),
  sellerTaxId: optionalText(value?.sellerTaxId),
  sellerEmail: optionalText(value?.sellerEmail),
  sellerPhone: optionalText(value?.sellerPhone),
  sellerAddressLine1: optionalText(value?.sellerAddressLine1),
  sellerAddressLine2: optionalText(value?.sellerAddressLine2),
  sellerCity: optionalText(value?.sellerCity),
  sellerStateOrProvince: optionalText(value?.sellerStateOrProvince),
  sellerPostalCode: optionalText(value?.sellerPostalCode),
  sellerCountry: optionalText(value?.sellerCountry),
  internalNote: optionalText(value?.internalNote),
  customerNote: optionalText(value?.customerNote),
  subtotal: asNumber(value?.subtotal),
  taxTotal: asNumber(value?.taxTotal),
  total: asNumber(value?.total),
  paidTotal: asNumber(value?.paidTotal),
  creditTotal: asNumber(value?.creditTotal),
  remaining: asNumber(value?.remaining),
  overpaid: asNumber(value?.overpaid),
  lines: Array.isArray(value?.lines) ? value.lines.map(normalizeLine) : [],
  payments: Array.isArray(value?.payments) ? value.payments.map(normalizePayment) : [],
  createdAt: optionalText(value?.createdAt),
  updatedAt: optionalText(value?.updatedAt),
});

const normalizeListItem = (value: any): InvoiceListItem => ({
  id: String(value?.id ?? ''),
  documentType: normalizeEnum(value?.documentType, documentTypes, 'invoice'),
  status: normalizeEnum(value?.status, statuses, 'draft'),
  number: optionalText(value?.number),
  issueDate: optionalText(value?.issueDate),
  dueDate: optionalText(value?.dueDate),
  isOverdue: Boolean(value?.isOverdue),
  currency: normalizeCurrencyCode(value?.currency),
  contactId: String(value?.contactId ?? ''),
  contactName: String(value?.contactName ?? ''),
  assetId: optionalText(value?.assetId),
  assetName: optionalText(value?.assetName),
  locationId: optionalText(value?.locationId),
  locationName: optionalText(value?.locationName),
  relatedInvoiceId: optionalText(value?.relatedInvoiceId),
  total: asNumber(value?.total),
  paidTotal: asNumber(value?.paidTotal),
  remaining: asNumber(value?.remaining),
  overpaid: asNumber(value?.overpaid),
  createdAt: optionalText(value?.createdAt),
  updatedAt: optionalText(value?.updatedAt),
});

const normalizeSettings = (value: any): InvoiceSettings => ({
  id: String(value?.id ?? ''),
  defaultCurrency: normalizeCurrencyCode(value?.defaultCurrency),
  invoicePrefix: String(value?.invoicePrefix ?? 'INV'),
  creditPrefix: String(value?.creditPrefix ?? 'CN'),
  defaultDueDays: asNumber(value?.defaultDueDays),
  pricesIncludeTax: Boolean(value?.pricesIncludeTax),
  useDayView: Boolean(value?.useDayView),
  roundingIncrement: asNumber(value?.roundingIncrement, 0.01),
  invoiceNextNumber: asNumber(value?.invoiceNextNumber, 1),
  creditNextNumber: asNumber(value?.creditNextNumber, 1),
  createdAt: optionalText(value?.createdAt),
  updatedAt: optionalText(value?.updatedAt),
});

const pickItems = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  return [];
};

const pickSingle = (payload: any): any => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data) && !payload.id) {
    return payload.data;
  }
  return payload;
};

const buildQuery = (path: string, query?: Record<string, string | number | boolean | undefined>): string => {
  if (!query) return path;
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === '') return;
    params.set(key, String(value));
  });
  const queryString = params.toString();
  return queryString ? `${path}?${queryString}` : path;
};

const notFoundToNull = async <T>(work: () => Promise<T>): Promise<T | null> => {
  try {
    return await work();
  } catch (error) {
    if (error instanceof Error && /404|not found/i.test(error.message)) {
      return null;
    }
    throw error;
  }
};

export const invoiceService = {
  getCurrencies: async (): Promise<CurrencyOption[]> => {
    const payload = await apiRequest<any>('/api/v1/invoices/currencies');
    return pickItems(payload).map(normalizeCurrencyOption);
  },

  getInvoices: async (query?: InvoiceListQuery): Promise<InvoicePagedResult> => {
    const payload = await apiRequest<PagedResult<any> | any[]>(buildQuery('/api/v1/invoices', {
      q: query?.q,
      contactId: query?.contactId,
      assetId: query?.assetId,
      locationId: query?.locationId,
      status: query?.status,
      documentType: query?.documentType,
      overdue: query?.overdue,
      from: query?.from,
      to: query?.to,
      paidFrom: query?.paidFrom,
      paidTo: query?.paidTo,
      issuedBefore: query?.issuedBefore,
      page: query?.page,
      pageSize: query?.pageSize,
    }));
    const items = pickItems(payload).map(normalizeListItem);
    const page = asNumber((payload as PagedResult<any>)?.page, query?.page ?? 1);
    const pageSize = asNumber((payload as PagedResult<any>)?.pageSize, query?.pageSize ?? items.length);
    const total = asNumber((payload as PagedResult<any>)?.total, items.length);
    return {
      items,
      page,
      pageSize,
      total,
      hasNext: Boolean((payload as PagedResult<any>)?.hasNext),
    };
  },

  getInvoiceSummary: async (from: string, to?: string, locationId?: string): Promise<InvoiceDaySummary> => {
    const payload = pickSingle(await apiRequest<any>(buildQuery('/api/v1/invoices/summary', { from, to, locationId })));
    const currencies = Array.isArray(payload?.currencies)
      ? payload.currencies.map(normalizeCurrencyCode)
      : [];
    return {
      currency: normalizeCurrencyCode(payload?.currency),
      currencies,
      mixedCurrencies: Boolean(payload?.mixedCurrencies) || currencies.length > 1,
      moneyIn: paymentTotals(payload?.moneyIn),
      issued: {
        count: asNumber(payload?.issued?.count),
        billedTotal: asNumber(payload?.issued?.billedTotal),
        remaining: asNumber(payload?.issued?.remaining),
        collections: paymentTotals(payload?.issued?.collections),
      },
      collectedOnEarlier: paymentTotals(payload?.collectedOnEarlier),
      draftCount: asNumber(payload?.draftCount),
    };
  },

  getCashDay: async (date: string, locationId?: string): Promise<CashDay> => {
    const item = pickSingle(await apiRequest<any>(buildQuery('/api/v1/cash-days', { date, locationId })));
    return normalizeCashDay(item);
  },

  upsertCashDay: async (request: UpsertCashDayRequest): Promise<CashDay> => {
    const item = pickSingle(await apiRequest<any>('/api/v1/cash-days', {
      method: 'PATCH',
      body: JSON.stringify(request),
    }));
    return normalizeCashDay(item);
  },

  createCashDayAdjustment: async (request: CreateCashDayAdjustmentRequest): Promise<CashDay> => {
    const item = pickSingle(await apiRequest<any>('/api/v1/cash-days/adjustments', {
      method: 'POST',
      body: JSON.stringify(request),
    }));
    return normalizeCashDay(item);
  },

  deleteCashDayAdjustment: async (adjustmentId: string): Promise<CashDay> => {
    const item = pickSingle(await apiRequest<any>(`/api/v1/cash-days/adjustments/${adjustmentId}`, {
      method: 'DELETE',
    }));
    return normalizeCashDay(item);
  },

  getInvoiceById: async (id: string): Promise<Invoice | null> =>
    notFoundToNull(async () => {
      const item = pickSingle(await apiRequest<any>(`/api/v1/invoices/${id}`));
      return item ? normalizeInvoice(item) : null;
    }),

  createInvoice: async (request: CreateInvoiceRequest): Promise<Invoice | null> => {
    const item = pickSingle(await apiRequest<any>('/api/v1/invoices', {
      method: 'POST',
      body: JSON.stringify(request),
    }));
    return item ? normalizeInvoice(item) : null;
  },

  updateInvoice: async (id: string, request: UpdateInvoiceRequest): Promise<Invoice | null> =>
    notFoundToNull(async () => {
      const item = pickSingle(await apiRequest<any>(`/api/v1/invoices/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(request),
      }));
      return item ? normalizeInvoice(item) : null;
    }),

  issueInvoice: async (id: string): Promise<Invoice | null> => {
    const item = pickSingle(await apiRequest<any>(`/api/v1/invoices/${id}/issue`, { method: 'POST' }));
    return item ? normalizeInvoice(item) : null;
  },

  voidInvoice: async (id: string): Promise<Invoice | null> => {
    const item = pickSingle(await apiRequest<any>(`/api/v1/invoices/${id}/void`, { method: 'POST' }));
    return item ? normalizeInvoice(item) : null;
  },

  deleteInvoice: async (id: string): Promise<void> => {
    await apiRequest(`/api/v1/invoices/${id}`, { method: 'DELETE' });
  },

  createCreditNote: async (invoiceId: string): Promise<Invoice | null> => {
    const item = pickSingle(await apiRequest<any>(`/api/v1/invoices/${invoiceId}/credit-notes`, { method: 'POST' }));
    return item ? normalizeInvoice(item) : null;
  },

  createPayment: async (invoiceId: string, request: CreateInvoicePaymentRequest): Promise<Invoice | null> => {
    const item = pickSingle(await apiRequest<any>(`/api/v1/invoices/${invoiceId}/payments`, {
      method: 'POST',
      body: JSON.stringify(request),
    }));
    return item ? normalizeInvoice(item) : null;
  },

  deletePayment: async (invoiceId: string, paymentId: string): Promise<Invoice | null> => {
    const item = pickSingle(await apiRequest<any>(`/api/v1/invoices/${invoiceId}/payments/${paymentId}`, {
      method: 'DELETE',
    }));
    return item ? normalizeInvoice(item) : null;
  },

  sendEmail: async (
    invoiceId: string,
    request: SendInvoiceEmailRequest & { pdf: Blob; fileName: string },
  ): Promise<Invoice | null> => {
    const formData = new FormData();
    formData.append('pdf', request.pdf, request.fileName);
    if (request.to?.trim()) formData.append('to', request.to.trim());
    if (request.subject?.trim()) formData.append('subject', request.subject.trim());
    if (request.message?.trim()) formData.append('message', request.message.trim());
    const item = pickSingle(await apiRequest<any>(`/api/v1/invoices/${invoiceId}/email`, {
      method: 'POST',
      body: formData,
    }));
    return item ? normalizeInvoice(item) : null;
  },

  getSettings: async (): Promise<InvoiceSettings> => {
    const item = pickSingle(await apiRequest<any>('/api/v1/invoice-settings'));
    return normalizeSettings(item);
  },

  updateSettings: async (request: UpdateInvoiceSettingsRequest): Promise<InvoiceSettings> => {
    const item = pickSingle(await apiRequest<any>('/api/v1/invoice-settings', {
      method: 'PATCH',
      body: JSON.stringify(request),
    }));
    return normalizeSettings(item);
  },
};

export const getCurrencies = invoiceService.getCurrencies;
export const getInvoices = invoiceService.getInvoices;
export const getInvoiceSummary = invoiceService.getInvoiceSummary;
export const getInvoiceById = invoiceService.getInvoiceById;
export const createInvoice = invoiceService.createInvoice;
export const updateInvoice = invoiceService.updateInvoice;
export const issueInvoice = invoiceService.issueInvoice;
export const voidInvoice = invoiceService.voidInvoice;
export const deleteInvoice = invoiceService.deleteInvoice;
export const createCreditNote = invoiceService.createCreditNote;
export const createPayment = invoiceService.createPayment;
export const deletePayment = invoiceService.deletePayment;
export const sendInvoiceEmail = invoiceService.sendEmail;
export const getInvoiceSettings = invoiceService.getSettings;
export const updateInvoiceSettings = invoiceService.updateSettings;
export const getCashDay = invoiceService.getCashDay;
export const upsertCashDay = invoiceService.upsertCashDay;
export const createCashDayAdjustment = invoiceService.createCashDayAdjustment;
export const deleteCashDayAdjustment = invoiceService.deleteCashDayAdjustment;
